// Mock data for We Want Clients website

export const heroData = {
  title: "We Want Clients",
  tagline: "AI That Converts Leads Into Revenue, Fast",
  subtitle: "24/7 AI-powered solutions for high-revenue home services, personal injury law firms, and CPA practices",
  cta: {
    primary: "Get Your Free AI Audit",
    primaryLink: "https://calendly.com/aiasap-info/ai-asap-consultation",
    secondary: "See How It Works"
  },
  stats: [
    { value: "500+", label: "Businesses Automated" },
    { value: "92%", label: "Lead Conversion Rate" },
    { value: "24/7", label: "Always Available" }
  ]
};

export const servicesData = [
  {
    id: 1,
    icon: "Bot",
    title: "AI Chatbot & Voice Agents",
    description: "Instant lead qualification, appointment booking, and customer support that never sleeps. Handles complex conversations naturally.",
    features: ["24/7 Availability", "Multi-language Support", "Smart Lead Scoring", "Natural Conversations"],
    targetAudience: ["Home Services", "Legal", "Accounting"]
  },
  {
    id: 2,
    icon: "Database",
    title: "CRM Integration & Automation",
    description: "Seamlessly connect your existing systems. Auto-sync leads, update records, and trigger workflows without manual data entry.",
    features: ["Salesforce Integration", "HubSpot Sync", "Auto Lead Assignment", "Pipeline Management"],
    targetAudience: ["Home Services", "Legal", "Accounting"]
  },
  {
    id: 3,
    icon: "TrendingUp",
    title: "Predictive Analytics & Insights",
    description: "Know which leads convert before your competition. AI-powered forecasting for revenue, seasonality, and market trends.",
    features: ["Lead Scoring AI", "Revenue Forecasting", "Churn Prediction", "Market Analysis"],
    targetAudience: ["Home Services", "Legal", "Accounting"]
  },
  {
    id: 4,
    icon: "Phone",
    title: "Intelligent Call Routing",
    description: "Route high-value calls to your best closers. AI screens, qualifies, and prioritizes based on conversion probability.",
    features: ["Smart Call Screening", "Priority Routing", "Call Analytics", "Real-time Transcription"],
    targetAudience: ["Home Services", "Legal"]
  },
  {
    id: 5,
    icon: "Calendar",
    title: "Automated Appointment Booking",
    description: "Let AI handle scheduling 24/7. Smart calendar management that reduces no-shows and maximizes your team's billable hours.",
    features: ["Calendar Sync", "Reminder Automation", "No-show Reduction", "Timezone Intelligence"],
    targetAudience: ["Home Services", "Legal", "Accounting"]
  },
  {
    id: 6,
    icon: "FileText",
    title: "Document Intelligence",
    description: "Extract insights from contracts, case files, and financial documents instantly. Automated data entry and compliance checks.",
    features: ["OCR Processing", "Data Extraction", "Compliance Checks", "Smart Filing"],
    targetAudience: ["Legal", "Accounting"]
  }
];

export const industryFocusData = [
  {
    id: 1,
    name: "High-Revenue Home Services",
    industries: ["Plumbing", "HVAC", "Roofing"],
    image: "https://images.pexels.com/photos/8985917/pexels-photo-8985917.jpeg",
    challenges: [
      "After-hours emergency calls going to voicemail",
      "Manual appointment scheduling eating up time",
      "Difficulty tracking lead sources and ROI"
    ],
    solutions: [
      "AI voice agents answer every call instantly",
      "Automated booking with smart calendar sync",
      "Real-time lead tracking and attribution"
    ],
    roi: "Average 3.2x ROI in first 6 months"
  },
  {
    id: 2,
    name: "Personal Injury Attorneys",
    industries: ["Personal Injury Law", "Accident Claims", "Legal Consultation"],
    image: "https://images.pexels.com/photos/6077797/pexels-photo-6077797.jpeg",
    challenges: [
      "Missing high-value calls from potential clients",
      "Manual intake process delays case evaluation",
      "Difficulty prioritizing case opportunities"
    ],
    solutions: [
      "AI screens and qualifies leads 24/7",
      "Automated intake forms with smart follow-up",
      "Predictive case value scoring"
    ],
    roi: "Average 4.1x ROI with 85% faster intake"
  },
  {
    id: 3,
    name: "CPA Firms & Accountants",
    industries: ["Tax Preparation", "Business Accounting", "Financial Advisory"],
    image: "https://images.pexels.com/photos/35501871/pexels-photo-35501871.jpeg",
    challenges: [
      "Seasonal volume spikes overwhelming staff",
      "Repetitive client questions taking up time",
      "Manual document collection slowing workflow"
    ],
    solutions: [
      "AI handles common queries automatically",
      "Smart document collection and organization",
      "Predictive workload management"
    ],
    roi: "Average 60% reduction in admin time"
  }
];

export const caseStudiesData = [
  {
    id: 1,
    company: "Elite Plumbing & HVAC",
    industry: "Home Services",
    location: "Dallas, TX",
    challenge: "Missing 60% of after-hours emergency calls, losing $180K annually in revenue",
    solution: "Implemented AI voice agent with 24/7 call handling and smart appointment booking",
    results: [
      { metric: "Call Answer Rate", before: "40%", after: "98%", improvement: "+145%" },
      { metric: "Monthly Revenue", before: "$85K", after: "$242K", improvement: "+185%" },
      { metric: "Booking Conversion", before: "23%", after: "67%", improvement: "+191%" }
    ],
    testimonial: "We Want Clients turned our missed calls into our biggest revenue driver. The ROI was immediate.",
    author: "Mike Henderson, Owner",
    timeframe: "6 months"
  },
  {
    id: 2,
    company: "Thompson & Associates Law",
    industry: "Personal Injury",
    location: "Phoenix, AZ",
    challenge: "Intake team overwhelmed, 48-hour response time losing cases to competitors",
    solution: "AI-powered intake system with instant qualification and CRM integration",
    results: [
      { metric: "Response Time", before: "48 hours", after: "3 minutes", improvement: "-99%" },
      { metric: "Qualified Leads", before: "45/month", after: "127/month", improvement: "+182%" },
      { metric: "Case Sign Rate", before: "32%", after: "61%", improvement: "+91%" }
    ],
    testimonial: "We went from losing cases to competitors to being the first firm prospects talk to. Game changer.",
    author: "Sarah Thompson, Managing Partner",
    timeframe: "4 months"
  },
  {
    id: 3,
    company: "Premier Tax & Advisory",
    industry: "CPA Services",
    location: "Chicago, IL",
    challenge: "Tax season chaos with 300+ daily client calls and document collection bottleneck",
    solution: "AI chatbot for FAQ handling and automated document intelligence system",
    results: [
      { metric: "Call Volume", before: "300/day", after: "450/day", improvement: "+50%" },
      { metric: "Document Processing", before: "6 hours", after: "45 minutes", improvement: "-88%" },
      { metric: "Client Satisfaction", before: "3.2/5", after: "4.8/5", improvement: "+50%" }
    ],
    testimonial: "Our team can finally focus on high-value work instead of answering the same questions 50 times a day.",
    author: "Robert Chen, CPA, Principal",
    timeframe: "One tax season"
  }
];

export const testimonialsData = [
  {
    id: 1,
    quote: "We doubled our revenue in 5 months. The AI voice agent captures every lead, even at 2 AM. It's like having our best salesperson working 24/7.",
    author: "Jennifer Martinez",
    role: "Owner, Martinez Roofing Solutions",
    company: "Martinez Roofing",
    industry: "Home Services",
    revenue: "$2.4M Annual",
    rating: 5
  },
  {
    id: 2,
    quote: "We Want Clients' predictive analytics helped us identify our highest-value cases before our competitors even knew they existed. Our case acceptance rate jumped 73%.",
    author: "David Wu",
    role: "Senior Partner",
    company: "Wu & Partners Legal",
    industry: "Personal Injury",
    revenue: "$8M Annual",
    rating: 5
  },
  {
    id: 3,
    quote: "The CRM integration was seamless. Every lead, every interaction, every document - automatically synced. We saved 40 hours per week on data entry alone.",
    author: "Amanda Foster",
    role: "Managing Director",
    company: "Foster Financial Group",
    industry: "CPA Services",
    revenue: "$1.8M Annual",
    rating: 5
  },
  {
    id: 4,
    quote: "I was skeptical about AI until I saw the numbers. 94% of our emergency calls are now answered instantly. That's pure profit we were leaving on the table.",
    author: "Carlos Rodriguez",
    role: "CEO",
    company: "Advanced HVAC Systems",
    industry: "Home Services",
    revenue: "$5.2M Annual",
    rating: 5
  }
];

export const pricingData = [
  {
    id: 1,
    name: "Starter",
    tagline: "Perfect for Growing Businesses",
    price: "$999",
    period: "per month",
    setupFee: "One-time setup fee applies",
    description: "Essential AI automation for businesses ready to scale",
    features: [
      { included: true, text: "AI Chatbot (up to 1,000 conversations/mo)" },
      { included: true, text: "Basic CRM Integration (1 platform)" },
      { included: true, text: "Email & SMS Automation" },
      { included: true, text: "Lead Capture & Qualification" },
      { included: true, text: "Performance Dashboard" },
      { included: false, text: "Voice Agent" },
      { included: false, text: "Predictive Analytics" },
      { included: false, text: "Advanced Integrations" }
    ],
    cta: "Start Free Trial",
    popular: false,
    limits: "1,000 conversations/month"
  },
  {
    id: 2,
    name: "Professional",
    tagline: "Most Popular for Serious Growth",
    price: "Custom",
    period: "tailored pricing",
    setupFee: "One-time setup fee applies",
    description: "Complete AI automation suite with voice and predictive intelligence",
    features: [
      { included: true, text: "AI Chatbot (up to 5,000 conversations/mo)" },
      { included: true, text: "AI Voice Agent (500 calls/mo)" },
      { included: true, text: "Advanced CRM Integration (3 platforms)" },
      { included: true, text: "Predictive Lead Scoring" },
      { included: true, text: "Automated Appointment Booking" },
      { included: true, text: "Smart Call Routing" },
      { included: true, text: "Real-time Analytics Dashboard" },
      { included: true, text: "Priority Support" }
    ],
    cta: "Get Started Now",
    popular: true,
    limits: "5,000 conversations + 500 calls/month"
  },
  {
    id: 3,
    name: "Enterprise",
    tagline: "For High-Volume Operations",
    price: "Custom",
    period: "tailored to your needs",
    setupFee: "Custom implementation",
    description: "Fully customized AI infrastructure with dedicated support",
    features: [
      { included: true, text: "Unlimited AI Conversations & Calls" },
      { included: true, text: "Multi-location Support" },
      { included: true, text: "Custom AI Training & Fine-tuning" },
      { included: true, text: "Advanced Predictive Analytics" },
      { included: true, text: "Document Intelligence & Processing" },
      { included: true, text: "Unlimited CRM Integrations" },
      { included: true, text: "Dedicated Account Manager" },
      { included: true, text: "White-label Options Available" }
    ],
    cta: "Contact Sales",
    popular: false,
    limits: "Unlimited"
  }
];

export const faqData = [
  {
    id: 1,
    question: "How quickly can we get up and running?",
    answer: "Most clients are live within 7-14 days. We handle the technical setup, AI training, and integration with your existing systems. You'll see results from day one."
  },
  {
    id: 2,
    question: "What if our industry is highly regulated (like legal or financial)?",
    answer: "We specialize in regulated industries. Our AI systems are compliant with HIPAA, GDPR, and industry-specific regulations. We provide audit trails, data encryption, and compliance documentation."
  },
  {
    id: 3,
    question: "Can the AI handle complex conversations or just basic FAQs?",
    answer: "Our AI handles sophisticated multi-turn conversations including objection handling, upselling, and appointment booking. It's trained on your specific business processes and continuously learns from interactions."
  },
  {
    id: 4,
    question: "What happens to leads when the AI can't answer something?",
    answer: "The AI seamlessly transfers to your team with full context. Every conversation is logged in your CRM with transcripts, lead scores, and recommended next actions."
  },
  {
    id: 5,
    question: "Do we need to replace our current systems?",
    answer: "No. We integrate with your existing CRM, phone systems, calendars, and tools. Our AI enhances what you have without requiring you to start over."
  },
  {
    id: 6,
    question: "What's the typical ROI timeframe?",
    answer: "Most clients see positive ROI within 60-90 days. Home services average 3.2x ROI in 6 months, personal injury firms see 4.1x, and CPA practices reduce admin costs by 60%."
  }
];

export const contactFormData = {
  title: "Ready to Transform Your Business?",
  subtitle: "Get a free AI readiness audit and custom implementation plan. No obligation, just insights.",
  fields: [
    { name: "fullName", label: "Full Name", type: "text", required: true },
    { name: "email", label: "Email Address", type: "email", required: true },
    { name: "phone", label: "Phone Number", type: "tel", required: true },
    { name: "company", label: "Company Name", type: "text", required: true },
    { name: "industry", label: "Industry", type: "select", required: true, options: [
      "Home Services (Plumbing, HVAC, Roofing)",
      "Personal Injury Law",
      "CPA / Accounting Services",
      "Other"
    ]},
    { name: "revenue", label: "Annual Revenue", type: "select", required: false, options: [
      "Under $500K",
      "$500K - $1M",
      "$1M - $5M",
      "$5M - $10M",
      "Over $10M"
    ]},
    { name: "message", label: "What's your biggest challenge?", type: "textarea", required: false }
  ]
};
